import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";
import companyLogo from "../../../assets/cogent-logo.jpeg";
import { getOrderDeliveryText } from "../orders/orderDeliveryText";
import { resolveQuotationGstKind, buildQuotationGstSummaryRows, buildFreightGstSummaryRows, formatFreightChargesLineLabel, getFreightNotesDisplay } from "../orders/quotationPdfShared";

const PDF_ORANGE = "#f97316";
const PDF_ORANGE_DARK = "#c2410c";
const PDF_ORANGE_BORDER = "#fdba74";

// Create StyleSheet for PDF
const styles = StyleSheet.create({
  page: {
    paddingTop: 35,
    paddingBottom: 35,
    paddingLeft: 40,
    paddingRight: 40,
    fontSize: 10,
    fontFamily: "Helvetica",
    backgroundColor: "#ffffff",
  },
  headerSection: {
    marginBottom: 15,
    paddingBottom: 10,
    borderBottomWidth: 2,
    borderBottomColor: PDF_ORANGE,
  },
  headerTitle: {
    textAlign: "center",
    fontSize: 16,
    fontWeight: "bold",
    color: "#1a1a1a",
    letterSpacing: 2,
    marginBottom: 10,
  },
  headerTop: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  headerLogoSide: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  headerSmallLogo: {
    width: 44,
    height: 44,
    objectFit: "contain",
    marginRight: 8,
  },
  companyLogo: {
    width: 42,
    height: 42,
    objectFit: "contain",
    marginRight: 10,
  },
  headerInfoSide: {
    flex: 1,
    alignItems: "flex-end",
    justifyContent: "center",
  },
  companyInfo: {
    flex: 1,
  },
  companyName: {
    fontSize: 13,
    fontWeight: "bold",
    color: "#1a1a1a",
    marginBottom: 2,
  },
  companyNameWithLogo: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 0,
  },
  companyTagline: {
    fontSize: 7,
    color: "#666",
    fontStyle: "italic",
    marginBottom: 2,
  },
  businessInfoText: {
    fontSize: 7.5,
    color: "#333",
    marginBottom: 1.5,
  },
  businessInfoLabel: {
    fontWeight: "bold",
    color: "#333",
  },
  quotationInfo: {
    textAlign: "right",
  },
  quotationType: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#333",
  },
  revisionText: {
    fontSize: 7,
    color: "#666",
    marginTop: 2,
  },
  quotationNumber: {
    fontSize: 18,
    fontWeight: "bold",
    color: PDF_ORANGE,
    marginTop: 3,
  },
  contactGrid: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 20,
  },
  contactColumn: {
    flex: 1,
  },
  contactLabel: {
    fontSize: 7,
    fontWeight: "bold",
    color: PDF_ORANGE,
    textTransform: "uppercase",
    marginBottom: 3,
  },
  contactText: {
    fontSize: 8,
    color: "#333",
    marginBottom: 2,
  },
  detailsGrid: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 15,
    marginBottom: 15,
  },
  detailsBox: {
    flex: 1,
    padding: 10,
    borderWidth: 1,
    borderRadius: 3,
  },
  customerBox: {
    borderColor: PDF_ORANGE_BORDER,
    backgroundColor: "#fff9f0",
  },
  datesBox: {
    borderColor: PDF_ORANGE_BORDER,
    backgroundColor: "#fff9f0",
  },
  detailsTitle: {
    fontSize: 7,
    fontWeight: "bold",
    color: PDF_ORANGE,
    textTransform: "uppercase",
    marginBottom: 4,
  },
  detailsGroup: {
    marginBottom: 3,
    fontSize: 8,
  },
  detailsLabel: {
    fontSize: 7,
    fontWeight: "bold",
    color: "#333",
  },
  detailsValue: {
    fontSize: 8,
    color: "#333",
  },
  customerName: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#000",
    marginBottom: 3,
  },
  tableWrapper: {
    marginBottom: 2,
  },
  tableHeader: {
    display: "flex",
    flexDirection: "row",
    backgroundColor: PDF_ORANGE,
    color: "#fff",
    borderBottomWidth: 1,
    borderBottomColor: PDF_ORANGE,
  },
  tableRow: {
    display: "flex",
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
    paddingVertical: 6,
  },
  tableHeaderCell: {
    fontSize: 8,
    fontWeight: "bold",
    paddingHorizontal: 4,
    paddingVertical: 6,
    textAlign: "center",
  },
  tableCell: {
    fontSize: 8,
    paddingHorizontal: 4,
    paddingVertical: 4,
  },
  colNum: {
    width: "5%",
    textAlign: "center",
    fontWeight: "bold",
    color: "#333",
  },
  colNumHeader: {
    width: "5%",
    textAlign: "center",
    fontWeight: "bold",
    color: "#fff",
  },
  colProduct: {
    width: "38%",
    textAlign: "left",
  },
  colQty: {
    width: "9%",
    textAlign: "center",
  },
  colArea: {
    width: "11%",
    textAlign: "center",
  },
  colUOM: {
    width: "8%",
    textAlign: "center",
  },
  colPrice: {
    width: "13%",
    textAlign: "right",
  },
  colAmount: {
    width: "16%",
    textAlign: "right",
  },
  productName: {
    fontSize: 8,
    fontWeight: "bold",
    color: "#000",
    marginBottom: 2,
  },
  productSpecs: {
    fontSize: 7,
    color: "#000",
    marginTop: 1,
  },
  amountText: {
    color: PDF_ORANGE_DARK,
    fontWeight: "bold",
  },
  termsTitle: {
    fontSize: 8,
    fontWeight: "bold",
    color: "#000",
    marginBottom: 4,
  },
  termsList: {
    fontSize: 7,
    color: "#333",
    lineHeight: 1.3,
  },
  summaryLabel: {
    fontSize: 8,
    color: "#333",
  },
  summaryValue: {
    fontSize: 8,
    color: "#333",
    fontWeight: "normal",
  },
  summaryValueNegative: {
    fontSize: 8,
    color: "#d32f2f",
    fontWeight: "normal",
  },
  // Image 2 layout styles
  summaryColumnsRow: {
    flexDirection: "row",
    borderWidth: 1,
    borderColor: PDF_ORANGE_BORDER,
    marginTop: 8,
  },
  amountBankCol: {
    flex: 1,
    padding: 8,
    borderRightWidth: 1,
    borderRightColor: PDF_ORANGE_BORDER,
    backgroundColor: "#fff9f0",
  },
  taxBreakdownCol: {
    width: "42%",
    backgroundColor: "#fff9f0",
  },
  taxRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderBottomWidth: 1,
    borderBottomColor: PDF_ORANGE_BORDER,
  },
  grandTotalBar: {
    flexDirection: "row",
    justifyContent: "space-between",
    backgroundColor: PDF_ORANGE,
    paddingHorizontal: 10,
    paddingVertical: 7,
  },
  grandTotalBarText: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#ffffff",
  },
  newFooterSection: {
    flexDirection: "row",
    borderWidth: 1,
    borderColor: PDF_ORANGE_BORDER,
    marginTop: 8,
  },
  termsFooterCol: {
    flex: 1,
    padding: 8,
    borderRightWidth: 1,
    borderRightColor: PDF_ORANGE_BORDER,
    backgroundColor: "#fff9f0",
  },
  signatureFooterCol: {
    width: "40%",
    padding: 8,
    alignItems: "center",
    backgroundColor: "#fff9f0",
  },
  stampImage: {
    width: 80,
    height: 80,
    marginBottom: 5,
  },
  footerText: {
    fontSize: 8,
    color: "#333",
    marginBottom: 3,
  },
  finalNote: {
    marginTop: 20,
    textAlign: "center",
    fontSize: 7,
    color: "#999",
    fontStyle: "italic",
    borderTopWidth: 1,
    borderTopColor: "#ddd",
    paddingTop: 8,
  },
});

export const QuotationTemplate = ({ order, settings }: any) => {
  const companyName = settings?.companyName || "Cogent Tech Solutions";
  const companyAddress = settings?.companyAddress || "";
  const companyPhone = settings?.companyPhone || "";
  const companyEmail = settings?.companyEmail || "";
  const companyGst = settings?.companyGst || "";
  const companyCountry = String(settings?.companyCountry ?? "India").trim() || "India";
  const companyState = String(settings?.companyState ?? "").trim();
  const bankAccountHolder = settings?.bankAccountHolder || "Cogent";
  const bankName = settings?.bankName || "";
  const bankAccountNo = settings?.bankAccountNo || "";
  const bankBranch = settings?.bankBranch || "";
  const bankIfsc = settings?.bankIfsc || "";

  const subTotal = parseFloat(order?.price) || 0;
  const transport = parseFloat(order?.freightCharges || order?.transport) || 0;
  const fixing = parseFloat(order?.fixing) || 0;
  const discount = parseFloat(order?.discount) || 0;

  // ── Number-to-words (Indian numbering system) ──────────────────────────
  const numberToWords = (n: number): string => {
    const ones = ["","One","Two","Three","Four","Five","Six","Seven","Eight","Nine","Ten","Eleven","Twelve","Thirteen","Fourteen","Fifteen","Sixteen","Seventeen","Eighteen","Nineteen"];
    const tens = ["","","Twenty","Thirty","Forty","Fifty","Sixty","Seventy","Eighty","Ninety"];
    const toWords = (num: number): string => {
      if (num === 0) return "";
      if (num < 20) return ones[num] + " ";
      if (num < 100) return tens[Math.floor(num / 10)] + " " + toWords(num % 10);
      if (num < 1000) return ones[Math.floor(num / 100)] + " Hundred " + toWords(num % 100);
      if (num < 100000) return toWords(Math.floor(num / 1000)) + "Thousand " + toWords(num % 1000);
      if (num < 10000000) return toWords(Math.floor(num / 100000)) + "Lakh " + toWords(num % 100000);
      return toWords(Math.floor(num / 10000000)) + "Crore " + toWords(num % 10000000);
    };
    const intPart = Math.floor(n);
    const decPart = Math.round((n - intPart) * 100);
    let result = toWords(intPart).trim() + " Rupees";
    if (decPart > 0) result += " and " + toWords(decPart).trim() + " Paise";
    return result + " only";
  };

  // ── GST breakdown per product rate ────────────────────────────────────
  const allProducts: any[] = order?.products || [];
  const gstRateMap: Record<number, number> = {};
  allProducts.forEach((p: any) => {
    const rate = parseFloat(p.gst?.toString() || "0");
    const base = parseFloat(p.price?.toString() || "0") * parseFloat(p.nos?.toString() || "1");
    if (rate > 0) {
      gstRateMap[rate] = (gstRateMap[rate] || 0) + base;
    }
  });
  const gstKind = resolveQuotationGstKind(
    String(order?.country_ ?? "India"),
    companyCountry,
    String(order?.state_ ?? ""),
    companyState,
  );
  const productGstRows = buildQuotationGstSummaryRows(gstRateMap, gstKind);
  const freightGstPct =
    parseFloat(
      String(
        (order as any)?.freightChargesGst ??
          (order as any)?.freight_charges_gst_ ??
          "",
      ).replace(/,/g, ""),
    ) || 0;
  const freightGstRows = buildFreightGstSummaryRows(transport, freightGstPct, gstKind);
  const freightTaxOnFreightOnly = freightGstRows.reduce((s, r) => s + r.amount, 0);
  const totalProductGst = productGstRows.reduce((sum, r) => sum + r.amount, 0);
  const totalGst = totalProductGst + freightTaxOnFreightOnly;
  const grandTotal = subTotal + transport + fixing + totalGst - discount;
  const roundOff = Math.round(grandTotal) - grandTotal;
  const grandTotalRounded = Math.round(grandTotal);

  // Helper function to get field value from camelCase or snake_case
  const getFieldValue = (camelCase: string, snakeCase: string) => {
    return order?.[camelCase] || order?.[snakeCase];
  };

  const formatDate = (date: any) => {
    if (!date) return "N/A";
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatIndiaTime = (dateString: string | Date | undefined) => {
    if (!dateString || dateString === "") return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    return date
      .toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: "Asia/Kolkata",
      })
      .replace(",", "");
  };

  const formatDateOnly = (
    dateString: string | Date | undefined
  ) => {
    if (!dateString || dateString === "") return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    return date
      .toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone: "Asia/Kolkata",
      })
      .replace(/\//g, "-");
  };

  // Header Component - Repeats on every page
  const Header = () => (
    <View style={styles.headerSection}>
      {/* Centered INVOICE title */}
      <Text style={styles.headerTitle}>INVOICE</Text>

      {/* Row: left = small logo + details */}
      <View style={styles.headerTop}>
        {/* Left Side: Small logo + company details */}
        <View style={styles.headerLogoSide}>
          <Image src={companyLogo} style={styles.companyLogo} />
        </View>
        <View style={styles.headerInfoSide}>
          <Text style={styles.companyName}>{companyName}</Text>
          {companyAddress ? <Text style={styles.businessInfoText}>{companyAddress}</Text> : null}
          {(companyPhone || companyEmail) ? <Text style={styles.businessInfoText}>{[companyPhone, companyEmail].filter(Boolean).join(" | ")}</Text> : null}
          {companyGst ? <Text style={styles.businessInfoText}>
            <Text style={styles.businessInfoLabel}>GSTIN: </Text>{companyGst}
            <Text> | </Text>
            <Text style={styles.businessInfoLabel}>State: </Text>33 - Tamil Nadu
          </Text> : null}
        </View>
      </View>
    </View>
  );

  // Details Section
  const DetailsSection = () => (
    <View style={styles.detailsGrid}>
      {/* Customer Details Box */}
      <View style={{ ...styles.detailsBox, ...styles.customerBox }}>
        <Text style={styles.detailsTitle}>Customer Details</Text>
        <Text style={styles.customerName}>{order.customerName}</Text>
        
        {order.company_name_ && (
          <Text style={styles.detailsGroup}>
            <Text style={styles.detailsLabel}>Company: </Text>
            {order.company_name_}
          </Text>
        )}
        
        {order.address_ && (
          <Text style={styles.detailsGroup}>
            <Text style={styles.detailsLabel}>Address: </Text>
            {order.address_}
          </Text>
        )}
        
        {(order.gst_ || order.customerGst_) && (
          <Text style={styles.detailsGroup}>
            <Text style={styles.detailsLabel}>GST: </Text>
            {order.gst_ || order.customerGst_}
          </Text>
        )}
        
        {order.mobile && (
          <Text style={styles.detailsGroup}>
            <Text style={styles.detailsLabel}>Mobile: </Text>
            {order.mobile}
          </Text>
        )}
        
        {(order.website_ || order.Website_) && (
          <Text style={styles.detailsGroup}>
            <Text style={styles.detailsLabel}>Website: </Text>
            {order.website_ || order.Website_}
          </Text>
        )}
      </View>

      {/* Important Dates Box */}
      <View style={{ ...styles.detailsBox, ...styles.datesBox }}>
        <Text style={styles.detailsTitle}>Important Dates</Text>
        
        {getFieldValue("createdAt", "created_at") && (
          <Text style={styles.detailsGroup}>
            <Text style={styles.detailsLabel}>Created On: </Text>
            {formatIndiaTime(getFieldValue("createdAt", "created_at"))}
          </Text>
        )}
        
        {getFieldValue("updatedAt", "updated_at") && (
          <Text style={styles.detailsGroup}>
            <Text style={styles.detailsLabel}>Updated Date: </Text>
            {formatIndiaTime(getFieldValue("updatedAt", "updated_at"))}
          </Text>
        )}
        
        {getOrderDeliveryText(order as any) ? (
          <Text style={styles.detailsGroup}>
            <Text style={styles.detailsLabel}>Delivery: </Text>
            {getOrderDeliveryText(order as any)}
          </Text>
        ) : null}
        
        {getFieldValue("toBeDelivered", "to_be_delivered") && (
          <Text style={styles.detailsGroup}>
            <Text style={styles.detailsLabel}>Delivered By Company: </Text>
            {formatDateOnly(getFieldValue("toBeDelivered", "to_be_delivered"))}
          </Text>
        )}
      </View>
    </View>
  );

  // Products Table Header
  const TableHeader = () => (
    <View style={styles.tableHeader}>
      <Text style={{ ...styles.tableHeaderCell, ...styles.colNumHeader }}>#</Text>
      <Text style={{ ...styles.tableHeaderCell, ...styles.colProduct }}>Product Name</Text>
      <Text style={{ ...styles.tableHeaderCell, ...styles.colQty }}>Qty</Text>
      <Text style={{ ...styles.tableHeaderCell, ...styles.colArea }}>Area</Text>
      <Text style={{ ...styles.tableHeaderCell, ...styles.colUOM }}>UOM</Text>
      <Text style={{ ...styles.tableHeaderCell, ...styles.colPrice }}>Price/Unit</Text>
      <Text style={{ ...styles.tableHeaderCell, ...styles.colAmount }}>Amount</Text>
    </View>
  );

  // Summary Section (Image 2 layout)
  const SummarySection = () => (
    <View>
      {/* Two-column row: Amount in Words + Bank Details | Tax Breakdown */}
      <View style={styles.summaryColumnsRow}>
        {/* Left: Amount in Words + Bank Details */}
        <View style={styles.amountBankCol}>
          <Text style={[styles.termsTitle, { color: PDF_ORANGE_DARK, marginBottom: 3 }]}>Quotation Amount In Words</Text>
          <Text style={[styles.termsList, { marginBottom: 8 }]}>{numberToWords(grandTotalRounded)}</Text>
          <Text style={[styles.termsTitle, { color: PDF_ORANGE_DARK, marginBottom: 3 }]}>Company Bank Details</Text>
          {bankAccountHolder ? <Text style={styles.termsList}>Account Holder Name : {bankAccountHolder}</Text> : null}
          {bankName ? <Text style={styles.termsList}>Bank Name : {bankName}</Text> : null}
          {bankAccountNo ? <Text style={styles.termsList}>A/c No : {bankAccountNo}</Text> : null}
          {bankBranch ? <Text style={styles.termsList}>Branch : {bankBranch}</Text> : null}
          {bankIfsc ? <Text style={styles.termsList}>IFSC Code : {bankIfsc}</Text> : null}
        </View>

        {/* Right: Tax Breakdown */}
        <View style={styles.taxBreakdownCol}>
          <View style={styles.taxRow}>
            <Text style={styles.summaryLabel}>Sub Total</Text>
            <Text style={styles.summaryValue}>₹{subTotal.toFixed(2)}</Text>
          </View>
          {fixing > 0 && (
            <View style={styles.taxRow}>
              <Text style={styles.summaryLabel}>Fixing</Text>
              <Text style={styles.summaryValue}>₹{fixing.toFixed(2)}</Text>
            </View>
          )}
          {discount > 0 && (
            <View style={styles.taxRow}>
              <Text style={styles.summaryLabel}>Discount</Text>
              <Text style={styles.summaryValueNegative}>-₹{discount.toFixed(2)}</Text>
            </View>
          )}
          {productGstRows.length > 0 ? (
            productGstRows.map((r) => (
              <View key={r.key} style={styles.taxRow}>
                <Text style={styles.summaryLabel}>{r.label}</Text>
                <Text style={styles.summaryValue}>₹{r.amount.toFixed(2)}</Text>
              </View>
            ))
          ) : null}
          {transport > 0 && (
            <View style={styles.taxRow}>
              <Text style={styles.summaryLabel}>{formatFreightChargesLineLabel(transport, freightGstPct)}</Text>
              <Text style={styles.summaryValue}>
                ₹{(freightGstPct > 0 && freightTaxOnFreightOnly > 0
                  ? transport + freightTaxOnFreightOnly
                  : transport
                ).toFixed(2)}
              </Text>
            </View>
          )}
          {Math.abs(roundOff) >= 0.01 && (
            <View style={styles.taxRow}>
              <Text style={styles.summaryLabel}>Round Off</Text>
              <Text style={styles.summaryValue}>₹{roundOff >= 0 ? "+" : ""}{roundOff.toFixed(2)}</Text>
            </View>
          )}
        </View>
      </View>

      {/* Grand Total full-width bar */}
      <View style={styles.grandTotalBar}>
        <Text style={styles.grandTotalBarText}>Grand Total</Text>
        <Text style={styles.grandTotalBarText}>₹{grandTotalRounded.toFixed(2)}</Text>
      </View>
    </View>
  );

  // Footer Section (Image 2 layout)
  const Footer = () => (
    <View style={styles.newFooterSection}>
      {/* Left: Terms and Conditions */}
      <View style={styles.termsFooterCol}>
        <Text style={styles.termsTitle}>Terms and Conditions</Text>
        {order.terms
          ? order.terms
              .split("\n")
              .filter((line: string) => line.trim())
              .map((line: string, index: number) => {
                const cleanedLine = line.trim().replace(/^[✓•–\-\*]\s*/, "").trim();
                return cleanedLine ? (
                  <Text key={index} style={styles.termsList}>• {cleanedLine}</Text>
                ) : null;
              })
          : null}
        {order.notes && (
          <View style={{ marginTop: order.terms ? 6 : 0 }}>
            <Text style={styles.termsTitle}>Special Notes</Text>
            <Text style={styles.termsList}>{order.notes}</Text>
          </View>
        )}
      </View>

      {/* Right: Company stamp + signature */}
      <View style={styles.signatureFooterCol}>
        <Text style={[styles.footerText, { fontWeight: "bold", marginBottom: 6 }]}>For: Cogent</Text>
        <Text style={[styles.footerText, { fontWeight: "bold" }]}>Authorized Signatory</Text>
      </View>
    </View>
  );

  // Calculate products per page based on available space
  const productsPerPage = 10;
  const totalPages = Math.max(1, Math.ceil(allProducts.length / productsPerPage));
  const pages = [];

  for (let i = 0; i < totalPages; i++) {
    const startIdx = i * productsPerPage;
    const endIdx = Math.min(startIdx + productsPerPage, allProducts.length);
    pages.push(allProducts.slice(startIdx, endIdx));
  }

  // Component to render product rows for a specific page
  const renderPageProducts = (pageProducts: any, startIndex: number) => (
    <>
      {pageProducts.map((p: any, index: number) => {
        const actualIndex = startIndex + index;
        return (
          <View key={actualIndex} style={styles.tableRow}>
            <Text style={{ ...styles.tableCell, ...styles.colNum }}>
              {actualIndex + 1}
            </Text>
            <View style={{ ...styles.tableCell, ...styles.colProduct, width: "38%" }}>
              <Text style={styles.productName}>{p.name || p.description || "N/A"}</Text>
              <Text style={styles.productSpecs}>
                {(!p.formulaType || p.formulaType === "width_height") && (
                  <>
                    W: {p.width}
                    {p.nool_width ? <>
                      <Text style={{ fontSize: 6 }}>{p.nool_width}</Text>
                    </> : ""}
                    {" "}{p.wh_uom}{" × "}H: {p.height}
                    {p.nool_height ? <>
                      <Text style={{ fontSize: 6 }}>{p.nool_height}</Text>
                    </> : ""}
                    {" "}{p.wh_uom}
                  </>
                )}

                {p.formulaType === "length" && (
                  <>L: {p.length} {p.length_uom}</>
                )}

                {p.specifications && Object.keys(p.specifications).length > 0 && (
                  <>
                    {" | "}
                    {Object.entries(p.specifications).map(
                      ([key, value]: [string, any], idx, arr) => (
                        <Text key={idx}>
                          {key}: {typeof value === "object" && value?.name ? value.name : value}
                          {idx < arr.length - 1 && ", "}
                        </Text>
                      )
                    )}
                  </>
                )}
              </Text>
            </View>
            <Text style={{ ...styles.tableCell, ...styles.colQty }}>
              {p.nos || "-"}
            </Text>
            <Text style={{ ...styles.tableCell, ...styles.colArea }}>
              {parseFloat(p.area || "0").toFixed(2)}
            </Text>
            <Text style={{ ...styles.tableCell, ...styles.colUOM }}>
              {p.area_uom || "-"}
            </Text>
            <Text style={{ ...styles.tableCell, ...styles.colPrice }}>
              Rs. {(parseFloat(p.price.toString()) / parseFloat(p.area || "1")).toFixed(2)}
            </Text>
            <Text style={{ ...styles.tableCell, ...styles.colAmount, ...styles.amountText }}>
              Rs. {parseFloat(p.price.toString()).toFixed(2)}
            </Text>
          </View>
        );
      })}
    </>
  );

  return (
    <Document title={`Quotation_${order.id}`}>
      {pages.map((pageProducts, pageIndex) => {
        const isFirstPage = pageIndex === 0;
        const isLastPage = pageIndex === pages.length - 1;
        const startIndex = pageIndex * productsPerPage;

        return (
          <Page key={pageIndex} size="A4" style={styles.page}>
            <Header />
            {isFirstPage && <DetailsSection />}
            <View style={styles.tableWrapper}>
              <TableHeader />
              {renderPageProducts(pageProducts, startIndex)}
            </View>
            {isLastPage && <SummarySection />}
            {isLastPage && <Footer />}
          </Page>
        );
      })}
    </Document>
  );
};

// ── Standalone Terms & Conditions document (merged via pdf-lib) ──────────────
const termsFormatDate = (dateString: string | Date | undefined) => {
  if (!dateString || dateString === "") return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "N/A";
  return date
    .toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Asia/Kolkata" })
    .replace(/\//g, "-");
};

export const QuotationTermsTemplate = ({ order }: { order: any }) => (
  <Document>
    <Page size="A4" style={{ paddingTop: 35, paddingBottom: 35, paddingLeft: 40, paddingRight: 40, fontSize: 10, fontFamily: "Helvetica", backgroundColor: "#ffffff" }}>
      <View style={{ alignItems: "center", marginBottom: 10 }}>
        <Text style={{ fontSize: 13, fontWeight: "bold", color: "#000" }}>Terms and Conditions</Text>
      </View>
      <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 12, borderBottomWidth: 1, borderBottomColor: "#ccc", paddingBottom: 6 }}>
        <Text style={{ fontSize: 8 }}>Q.No : {order.quotationNumber || order.invoiceId || order.id}</Text>
        <Text style={{ fontSize: 8 }}>Date : {order.invoiceDate ? termsFormatDate(order.invoiceDate) : termsFormatDate(order.createdAt)}</Text>
      </View>
      <View style={{ marginBottom: 6 }}>
        <Text style={{ fontSize: 8, fontWeight: "bold" }}>1. Price Basis</Text>
        <Text style={{ fontSize: 7, marginTop: 2 }}>The Prices quoted are Ex-works Chennai. GST @ 18% Extra.</Text>
      </View>
      <View style={{ marginBottom: 6 }}>
        <Text style={{ fontSize: 8, fontWeight: "bold" }}>2. Packing and Forwarding</Text>
        <Text style={{ fontSize: 7, marginTop: 2 }}>Packing shall be of standard packing at No cost</Text>
      </View>
      <View style={{ marginBottom: 6 }}>
        <Text style={{ fontSize: 8, fontWeight: "bold" }}>3. Freight Charges</Text>
        <Text style={{ fontSize: 7, marginTop: 2 }}>{getFreightNotesDisplay(order)}</Text>
      </View>
      <View style={{ marginBottom: 6 }}>
        <Text style={{ fontSize: 8, fontWeight: "bold" }}>4. Insurance</Text>
        <Text style={{ fontSize: 7, marginTop: 2 }}>In your Scope</Text>
      </View>
      <View style={{ marginBottom: 6 }}>
        <Text style={{ fontSize: 8, fontWeight: "bold" }}>5. Delivery</Text>
        <Text style={{ fontSize: 7, marginTop: 2 }}>{getOrderDeliveryText(order as any) || ""}</Text>
      </View>
      <View style={{ marginBottom: 6 }}>
        <Text style={{ fontSize: 8, fontWeight: "bold" }}>6. Validity</Text>
        <Text style={{ fontSize: 7, marginTop: 2 }}>{order.validity || ""}</Text>
      </View>
      <View style={{ marginBottom: 6 }}>
        <Text style={{ fontSize: 8, fontWeight: "bold" }}>7. Payment Terms</Text>
        <Text style={{ fontSize: 7, marginTop: 2 }}>{order.terms || ""}</Text>
      </View>
      <View style={{ marginBottom: 6 }}>
        <Text style={{ fontSize: 8, fontWeight: "bold" }}>8. Warranty</Text>
        <Text style={{ fontSize: 7, marginTop: 2 }}>12 months from the date supply only for manufacturing defects.</Text>
      </View>
      <View style={{ marginBottom: 6 }}>
        <Text style={{ fontSize: 8, fontWeight: "bold" }}>9. Statutory Variations</Text>
        <Text style={{ fontSize: 7, marginTop: 2 }}>Any impact on the price due to variation in taxes and duties applicable to Vendor scope of supply shall be to Purchaser account at actual against documentary evidence. Statutory variations shall include any variation in existing statutes or due to a new statute.</Text>
        <Text style={{ fontSize: 7, marginTop: 3 }}>The scope of statutory variations shall cover changes in rates of taxes and duties as well as scope of applicability and basis of valuation.</Text>
      </View>
      <View style={{ marginBottom: 6 }}>
        <Text style={{ fontSize: 8, fontWeight: "bold" }}>10. Force Majeure</Text>
        <Text style={{ fontSize: 7, marginTop: 2 }}>Neither the Vendor nor the Purchaser shall be liable for delays caused by force majeure conditions at either end.</Text>
        <Text style={{ fontSize: 7, marginTop: 2, marginLeft: 8 }}>Acts of GOD</Text>
        <Text style={{ fontSize: 7, marginLeft: 8 }}>Fires, Epidemics, Floods, Riots, Wars</Text>
        <Text style={{ fontSize: 7, marginLeft: 8 }}>Sabotage, Strikes, Lock-outs</Text>
      </View>
      <View style={{ marginBottom: 6 }}>
        <Text style={{ fontSize: 8, fontWeight: "bold" }}>11. Order Placement</Text>
        <Text style={{ fontSize: 7, marginTop: 2, marginLeft: 8 }}>M/s Cogent, No - 1/7, Sethu Rama Iyer Street, Ramapuram, Chennai - 600089</Text>
      </View>
      <View style={{ marginBottom: 6 }}>
        <Text style={{ fontSize: 8, fontWeight: "bold" }}>12. Cancel control</Text>
        <Text style={{ fontSize: 7, marginTop: 2 }}>Cancellation of the purchase order cannot be accepted and advance payment are non refundable.</Text>
      </View>
      <View style={{ marginTop: 20, borderTopWidth: 1, borderTopColor: "#ccc", paddingTop: 6, alignItems: "center" }}>
        <Text style={{ fontSize: 7, color: "#555" }}>Automation towards Innovation</Text>
      </View>
    </Page>
  </Document>
);

// ── Combined Template: main pages + terms page in ONE Document (single pdf() call) ──
export const CombinedQuotationTemplate = ({ order, settings }: { order: any; settings?: any }) => {
  const companyName = settings?.companyName || "Cogent Tech Solutions";
  const companyAddress = settings?.companyAddress || "";
  const companyPhone = settings?.companyPhone || "";
  const companyEmail = settings?.companyEmail || "";
  const companyGst = settings?.companyGst || "";
  const companyCountry = String(settings?.companyCountry ?? "India").trim() || "India";
  const companyState = String(settings?.companyState ?? "").trim();
  const bankAccountHolder = settings?.bankAccountHolder || "Cogent";
  const bankName = settings?.bankName || "";
  const bankAccountNo = settings?.bankAccountNo || "";
  const bankBranch = settings?.bankBranch || "";
  const bankIfsc = settings?.bankIfsc || "";

  const subTotal = parseFloat(order?.price) || 0;
  const transport = parseFloat(order?.freightCharges || order?.transport) || 0;
  const fixing = parseFloat(order?.fixing) || 0;
  const discount = parseFloat(order?.discount) || 0;

  const numberToWords = (n: number): string => {
    const ones = ["","One","Two","Three","Four","Five","Six","Seven","Eight","Nine","Ten","Eleven","Twelve","Thirteen","Fourteen","Fifteen","Sixteen","Seventeen","Eighteen","Nineteen"];
    const tens = ["","","Twenty","Thirty","Forty","Fifty","Sixty","Seventy","Eighty","Ninety"];
    const toWords = (num: number): string => {
      if (num === 0) return "";
      if (num < 20) return ones[num] + " ";
      if (num < 100) return tens[Math.floor(num / 10)] + " " + toWords(num % 10);
      if (num < 1000) return ones[Math.floor(num / 100)] + " Hundred " + toWords(num % 100);
      if (num < 100000) return toWords(Math.floor(num / 1000)) + "Thousand " + toWords(num % 1000);
      if (num < 10000000) return toWords(Math.floor(num / 100000)) + "Lakh " + toWords(num % 100000);
      return toWords(Math.floor(num / 10000000)) + "Crore " + toWords(num % 10000000);
    };
    const intPart = Math.floor(n);
    const decPart = Math.round((n - intPart) * 100);
    let result = toWords(intPart).trim() + " Rupees";
    if (decPart > 0) result += " and " + toWords(decPart).trim() + " Paise";
    return result + " only";
  };

  const allProducts: any[] = order?.products || [];
  const gstRateMap: Record<number, number> = {};
  allProducts.forEach((p: any) => {
    const rate = parseFloat(p.gst?.toString() || "0");
    const base = parseFloat(p.price?.toString() || "0") * parseFloat(p.nos?.toString() || "1");
    if (rate > 0) gstRateMap[rate] = (gstRateMap[rate] || 0) + base;
  });
  const gstKind = resolveQuotationGstKind(
    String(order?.country_ ?? "India"),
    companyCountry,
    String(order?.state_ ?? ""),
    companyState,
  );
  const productGstRows = buildQuotationGstSummaryRows(gstRateMap, gstKind);
  const freightGstPct =
    parseFloat(
      String(
        (order as any)?.freightChargesGst ??
          (order as any)?.freight_charges_gst_ ??
          "",
      ).replace(/,/g, ""),
    ) || 0;
  const freightGstRows = buildFreightGstSummaryRows(transport, freightGstPct, gstKind);
  const freightTaxOnFreightOnly = freightGstRows.reduce((s, r) => s + r.amount, 0);
  const totalProductGst = productGstRows.reduce((sum, r) => sum + r.amount, 0);
  const totalGst = totalProductGst + freightTaxOnFreightOnly;
  const grandTotal = subTotal + transport + fixing + totalGst - discount;
  const roundOff = Math.round(grandTotal) - grandTotal;
  const grandTotalRounded = Math.round(grandTotal);

  const getFieldValue = (camelCase: string, snakeCase: string) =>
    order?.[camelCase] || order?.[snakeCase];

  const fmt = (date: any) => {
    if (!date) return "N/A";
    const d = new Date(date);
    return `${String(d.getDate()).padStart(2,"0")}-${String(d.getMonth()+1).padStart(2,"0")}-${d.getFullYear()}`;
  };
  const fmtIndia = (ds: string | Date | undefined) => {
    if (!ds || ds === "") return "N/A";
    const d = new Date(ds);
    if (isNaN(d.getTime())) return "N/A";
    return d.toLocaleDateString("en-IN",{day:"2-digit",month:"2-digit",year:"numeric",hour:"2-digit",minute:"2-digit",hour12:false,timeZone:"Asia/Kolkata"}).replace(",","");
  };
  const fmtDateOnly = (ds: string | Date | undefined) => {
    if (!ds || ds === "") return "N/A";
    const d = new Date(ds);
    if (isNaN(d.getTime())) return "N/A";
    return d.toLocaleDateString("en-IN",{day:"2-digit",month:"2-digit",year:"numeric",timeZone:"Asia/Kolkata"}).replace(/\//g,"-");
  };

  const productsPerPage = 10;
  const totalPages = Math.max(1, Math.ceil(allProducts.length / productsPerPage));
  const pages: any[][] = [];
  for (let i = 0; i < totalPages; i++) {
    pages.push(allProducts.slice(i * productsPerPage, Math.min((i + 1) * productsPerPage, allProducts.length)));
  }

  const pageStyle = { paddingTop: 35, paddingBottom: 35, paddingLeft: 40, paddingRight: 40, fontSize: 10, fontFamily: "Helvetica", backgroundColor: "#ffffff" };

  const Header = () => (
    <View style={styles.headerSection}>
      <Text style={styles.headerTitle}>QUOTATION</Text>
      <View style={styles.headerTop}>
        <View style={styles.headerLogoSide}>
          <Image src={companyLogo} style={styles.companyLogo} />
        </View>
        <View style={styles.headerInfoSide}>
          <Text style={styles.companyName}>{companyName}</Text>
          {companyAddress ? <Text style={styles.businessInfoText}>{companyAddress}</Text> : null}
          {(companyPhone || companyEmail) ? <Text style={styles.businessInfoText}>{[companyPhone, companyEmail].filter(Boolean).join(" | ")}</Text> : null}
          {companyGst ? <Text style={styles.businessInfoText}>
            <Text style={styles.businessInfoLabel}>GSTIN: </Text>{companyGst}
            <Text> | </Text>
            <Text style={styles.businessInfoLabel}>State: </Text>33 - Tamil Nadu
          </Text> : null}
        </View>
      </View>
    </View>
  );

  const DetailsSection = () => (
    <View style={styles.detailsGrid}>
      <View style={{ ...styles.detailsBox, ...styles.customerBox }}>
        <Text style={styles.detailsTitle}>Quotation For</Text>
        {order.company_name_ && <Text style={styles.detailsGroup}><Text style={styles.detailsLabel}>Company Name: </Text>{order.company_name_}</Text>}
        {order.address_ && <Text style={styles.detailsGroup}><Text style={styles.detailsLabel}>Address: </Text>{order.address_}</Text>}
        {order.mobile && <Text style={styles.detailsGroup}><Text style={styles.detailsLabel}>Contact No: </Text>{order.mobile}</Text>}
        {order.customerName && <Text style={styles.detailsGroup}><Text style={styles.detailsLabel}>Person Name: </Text>{order.customerName}</Text>}
        {(order.gst_ || order.customerGst_) && <Text style={styles.detailsGroup}><Text style={styles.detailsLabel}>GSTIN: </Text>{order.gst_ || order.customerGst_}<Text style={styles.detailsLabel}> | State: </Text>Tamil Nadu</Text>}
      </View>
      <View style={{ ...styles.detailsBox, ...styles.datesBox }}>
        <Text style={styles.detailsTitle}>Quotation Details</Text>
        <Text style={styles.detailsGroup}><Text style={styles.detailsLabel}>Quotation No: </Text>{order.quotationNumber || order.invoiceId || order.id}</Text>
        <Text style={styles.detailsGroup}><Text style={styles.detailsLabel}>Date: </Text>{fmt(getFieldValue("invoiceDate","invoice_date") || getFieldValue("createdAt","created_at"))}</Text>
        <Text style={styles.detailsGroup}><Text style={styles.detailsLabel}>Delivery: </Text>{getOrderDeliveryText(order as any) || "N/A"}</Text>
        <Text style={styles.detailsGroup}><Text style={styles.detailsLabel}>Validity: </Text>{order.validity || "N/A"}</Text>
      </View>
    </View>
  );

  const TableHeader = () => (
    <View style={styles.tableHeader}>
      <Text style={{ ...styles.tableHeaderCell, ...styles.colNumHeader }}>#</Text>
      <Text style={{ ...styles.tableHeaderCell, ...styles.colProduct }}>Product Name</Text>
      <Text style={{ ...styles.tableHeaderCell, ...styles.colQty }}>Qty</Text>
      <Text style={{ ...styles.tableHeaderCell, ...styles.colArea }}>Area</Text>
      <Text style={{ ...styles.tableHeaderCell, ...styles.colUOM }}>UOM</Text>
      <Text style={{ ...styles.tableHeaderCell, ...styles.colPrice }}>Price/Unit</Text>
      <Text style={{ ...styles.tableHeaderCell, ...styles.colAmount }}>Amount</Text>
    </View>
  );

  const renderRows = (prods: any[], startIdx: number) => (
    <>
      {prods.map((p: any, i: number) => (
        <View key={startIdx + i} style={styles.tableRow}>
          <Text style={{ ...styles.tableCell, ...styles.colNum }}>{startIdx + i + 1}</Text>
          <View style={{ ...styles.tableCell, ...styles.colProduct }}>
            <Text style={styles.productName}>{p.name || p.description || "N/A"}</Text>
          </View>
          <Text style={{ ...styles.tableCell, ...styles.colQty }}>{p.nos || "-"}</Text>
          <Text style={{ ...styles.tableCell, ...styles.colArea }}>{parseFloat(p.area || "0").toFixed(2)}</Text>
          <Text style={{ ...styles.tableCell, ...styles.colUOM }}>{p.area_uom || "-"}</Text>
          <Text style={{ ...styles.tableCell, ...styles.colPrice }}>Rs. {(parseFloat(p.price.toString()) / parseFloat(p.area || "1")).toFixed(2)}</Text>
          <Text style={{ ...styles.tableCell, ...styles.colAmount, ...styles.amountText }}>Rs. {parseFloat(p.price.toString()).toFixed(2)}</Text>
        </View>
      ))}
    </>
  );

  const SummarySection = () => (
    <View style={styles.summaryColumnsRow}>
      <View style={styles.amountBankCol}>
        <Text style={[styles.termsTitle, { marginBottom: 3 }]}>Quotation Amount In Words</Text>
        <Text style={[styles.termsList, { marginBottom: 8 }]}>{numberToWords(grandTotalRounded)}</Text>
        <Text style={[styles.termsTitle, { marginBottom: 3 }]}>Company Bank Details</Text>
        {bankAccountHolder ? <Text style={styles.termsList}>Account Holder Name : {bankAccountHolder}</Text> : null}
        {bankName ? <Text style={styles.termsList}>Bank Name : {bankName}</Text> : null}
        {bankAccountNo ? <Text style={styles.termsList}>A/c No : {bankAccountNo}</Text> : null}
        {bankBranch ? <Text style={styles.termsList}>Branch : {bankBranch}</Text> : null}
        {bankIfsc ? <Text style={styles.termsList}>IFSC Code : {bankIfsc}</Text> : null}
        {bankAccountNo ? <Text style={styles.termsList}>A/c No : {bankAccountNo}</Text> : null}
        {bankBranch ? <Text style={styles.termsList}>Branch : {bankBranch}</Text> : null}
        {bankIfsc ? <Text style={styles.termsList}>IFSC Code : {bankIfsc}</Text> : null}
      </View>
      <View style={styles.taxBreakdownCol}>
        <View style={styles.taxRow}><Text style={styles.summaryLabel}>Sub Total</Text><Text style={styles.summaryValue}>₹{subTotal.toFixed(2)}</Text></View>
        {fixing > 0 && <View style={styles.taxRow}><Text style={styles.summaryLabel}>Fixing</Text><Text style={styles.summaryValue}>₹{fixing.toFixed(2)}</Text></View>}
        {discount > 0 && <View style={styles.taxRow}><Text style={styles.summaryLabel}>Discount</Text><Text style={styles.summaryValueNegative}>-₹{discount.toFixed(2)}</Text></View>}
        {productGstRows.length > 0 ? productGstRows.map((r) => (
          <View key={r.key} style={styles.taxRow}>
            <Text style={styles.summaryLabel}>{r.label}</Text>
            <Text style={styles.summaryValue}>₹{r.amount.toFixed(2)}</Text>
          </View>
        )) : null}
        {transport > 0 && (
          <View style={styles.taxRow}>
            <Text style={styles.summaryLabel}>{formatFreightChargesLineLabel(transport, freightGstPct)}</Text>
            <Text style={styles.summaryValue}>
              ₹{(freightGstPct > 0 && freightTaxOnFreightOnly > 0 ? transport + freightTaxOnFreightOnly : transport).toFixed(2)}
            </Text>
          </View>
        )}
        {Math.abs(roundOff) >= 0.01 && <View style={styles.taxRow}><Text style={styles.summaryLabel}>Round Off</Text><Text style={styles.summaryValue}>₹{roundOff >= 0 ? "+" : ""}{roundOff.toFixed(2)}</Text></View>}
      </View>
    </View>
  );

  const GrandTotalBar = () => (
    <View style={styles.grandTotalBar}>
      <Text style={styles.grandTotalBarText}>Grand Total</Text>
      <Text style={styles.grandTotalBarText}>₹{grandTotalRounded.toFixed(2)}</Text>
    </View>
  );

  const Footer = () => (
    <View style={styles.newFooterSection}>
      <View style={styles.termsFooterCol}>
        <Text style={styles.termsTitle}>Payment Terms</Text>
        {order.terms
          ? order.terms.split("\n").filter((l: string) => l.trim()).map((l: string, i: number) => {
              const cl = l.trim().replace(/^[✓•–\-\*]\s*/, "").trim();
              return cl ? <Text key={i} style={styles.termsList}>• {cl}</Text> : null;
            })
          : null}
        {order.notes && (
          <View style={{ marginTop: order.terms ? 6 : 0 }}>
            <Text style={styles.termsTitle}>Special Notes</Text>
            <Text style={styles.termsList}>{order.notes}</Text>
          </View>
        )}
      </View>
      <View style={styles.signatureFooterCol}>
        <Text style={[styles.footerText, { fontWeight: "bold", marginBottom: 6 }]}>For: Cogent</Text>
        {/* <Image src={stampImage} style={styles.stampImage} /> */}
        <Text style={[styles.footerText, { fontWeight: "bold" }]}>Authorized Signatory</Text>
      </View>
    </View>
  );

  // Build a flat array: all product pages (terms content force-breaks onto a new page inside the last Page)
  const allPages = [
    ...pages.map((pageProducts: any[], pageIndex: number) => {
      const isFirstPage = pageIndex === 0;
      const isLastPage = pageIndex === pages.length - 1;
      const startIndex = pageIndex * productsPerPage;
      return (
        <Page key={`product-${pageIndex}`} size="A4" style={styles.page}>
          <Header />
          {isFirstPage && <DetailsSection />}
          <View style={styles.tableWrapper}>
            <TableHeader />
            {renderRows(pageProducts, startIndex)}
          </View>
          {isLastPage && <SummarySection />}
          {isLastPage && <GrandTotalBar />}
          {isLastPage && <Footer />}
          {isLastPage && (
            <View break style={{ paddingTop: 35, paddingBottom: 35, paddingLeft: 0, paddingRight: 0 }}>
              <View style={{ alignItems: "center", marginBottom: 10 }}>
                <Text style={{ fontSize: 13, fontWeight: "bold", color: "#000" }}>Terms and Conditions</Text>
              </View>
              <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 12, borderBottomWidth: 1, borderBottomColor: "#ccc", paddingBottom: 6 }}>
                <Text style={{ fontSize: 8 }}>Q.No : {order.quotationNumber || order.invoiceId || order.id}</Text>
                <Text style={{ fontSize: 8 }}>Date : {order.invoiceDate ? fmtDateOnly(order.invoiceDate) : fmtDateOnly(order.createdAt)}</Text>
              </View>
              <View style={{ marginBottom: 6 }}><Text style={{ fontSize: 8, fontWeight: "bold" }}>1. Price Basis</Text><Text style={{ fontSize: 7, marginTop: 2 }}>The Prices quoted are Ex-works Chennai. GST @ 18% Extra.</Text></View>
              <View style={{ marginBottom: 6 }}><Text style={{ fontSize: 8, fontWeight: "bold" }}>2. Packing and Forwarding</Text><Text style={{ fontSize: 7, marginTop: 2 }}>Packing shall be of standard packing at No cost</Text></View>
              <View style={{ marginBottom: 6 }}><Text style={{ fontSize: 8, fontWeight: "bold" }}>3. Freight Charges</Text><Text style={{ fontSize: 7, marginTop: 2 }}>{getFreightNotesDisplay(order)}</Text></View>
              <View style={{ marginBottom: 6 }}><Text style={{ fontSize: 8, fontWeight: "bold" }}>4. Insurance</Text><Text style={{ fontSize: 7, marginTop: 2 }}>In your Scope</Text></View>
              <View style={{ marginBottom: 6 }}><Text style={{ fontSize: 8, fontWeight: "bold" }}>5. Delivery</Text><Text style={{ fontSize: 7, marginTop: 2 }}>{getOrderDeliveryText(order as any) || ""}</Text></View>
              <View style={{ marginBottom: 6 }}><Text style={{ fontSize: 8, fontWeight: "bold" }}>6. Validity</Text><Text style={{ fontSize: 7, marginTop: 2 }}>{order.validity || ""}</Text></View>
              <View style={{ marginBottom: 6 }}><Text style={{ fontSize: 8, fontWeight: "bold" }}>7. Payment Terms</Text><Text style={{ fontSize: 7, marginTop: 2 }}>{order.terms || ""}</Text></View>
              <View style={{ marginBottom: 6 }}><Text style={{ fontSize: 8, fontWeight: "bold" }}>8. Warranty</Text><Text style={{ fontSize: 7, marginTop: 2 }}>12 months from the date supply only for manufacturing defects.</Text></View>
              <View style={{ marginBottom: 6 }}><Text style={{ fontSize: 8, fontWeight: "bold" }}>9. Statutory Variations</Text><Text style={{ fontSize: 7, marginTop: 2 }}>Any impact on the price due to variation in taxes and duties applicable to Vendor scope of supply shall be to Purchaser account at actual against documentary evidence.</Text></View>
              <View style={{ marginBottom: 6 }}><Text style={{ fontSize: 8, fontWeight: "bold" }}>10. Force Majeure</Text><Text style={{ fontSize: 7, marginTop: 2 }}>Neither the Vendor nor the Purchaser shall be liable for delays caused by force majeure conditions at either end.</Text></View>
              <View style={{ marginBottom: 6 }}><Text style={{ fontSize: 8, fontWeight: "bold" }}>11. Order Placement</Text><Text style={{ fontSize: 7, marginTop: 2, marginLeft: 8 }}>M/s Cogent, No - 1/7, Sethu Rama Iyer Street, Ramapuram, Chennai - 600089</Text></View>
              <View style={{ marginBottom: 6 }}><Text style={{ fontSize: 8, fontWeight: "bold" }}>12. Cancel control</Text><Text style={{ fontSize: 7, marginTop: 2 }}>Cancellation of the purchase order cannot be accepted and advance payment are non refundable.</Text></View>
              <View style={{ marginTop: 20, borderTopWidth: 1, borderTopColor: "#ccc", paddingTop: 6, alignItems: "center" }}>
                <Text style={{ fontSize: 7, color: "#555" }}>Automation towards Innovation</Text>
              </View>
            </View>
          )}
        </Page>
      );
    }),
  ];

  return (
    <Document title={`Quotation_${order.id}`}>
      {allPages}
    </Document>
  );
};